import { useRef, useState } from "react";
import { Link } from "wouter";
import { ChevronDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useI18n } from "@/hooks/use-i18n";
import { useLocalizedUrl } from "@/hooks/use-localized-url";
import { useMenuCounts } from "@/hooks/use-menu-counts";
import { useAudioguidesAvailability } from "@/hooks/use-audioguides-availability";

import { MAIN_NAV, type NavCounts, type NavMenu } from "./nav-data";
import { useIsActive } from "./use-is-active";
import { CountPill } from "./count-pill";
import { NewBadge } from "./new-badge";

// Navigazione desktop. Tendine (shadcn DropdownMenu) con open controllato:
// onMouseEnter apre, onMouseLeave chiude con un piccolo ritardo, click sul trigger fa toggle.
// Tendina del sistema: bianca, bordo sottile, ombra da overlay, voci da 40 con icona grigia.

// Voce del menu (design system): 44px, 14px medio, bianco attenuato; al passaggio un fondo appena più chiaro.
// La voce attiva ha un filo bianco sottile rientrato sotto il testo.
const TRIGGER_BASE =
  "relative inline-flex h-11 items-center gap-[5px] rounded-lg px-3.5 text-md font-medium transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 data-[state=open]:bg-white/10 data-[state=open]:text-white";
const ACTIVE_LINE =
  "after:absolute after:bottom-0.5 after:left-3.5 after:right-3.5 after:h-[1.5px] after:rounded-sm after:bg-white/90 after:content-['']";

function navColor(active: boolean): string {
  return active ? "text-white" : "text-white/[0.88]";
}

function navClass(active: boolean, showLine = active): string {
  return `${TRIGGER_BASE} ${navColor(active)} ${showLine ? ACTIVE_LINE : ""}`;
}

function HoverDropdown({
  menu,
  language,
  isActive,
  getLocalizedUrl,
  counts,
}: {
  menu: NavMenu;
  language: string;
  isActive: (path: string) => boolean;
  getLocalizedUrl: (path: string) => string;
  counts?: NavCounts;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const triggerActive = menu.activePaths.some((path) => isActive(path));
  // Filo solo se attivo E menu chiuso: con la tendina aperta basta il fondo
  const showLine = triggerActive && !open;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger
        className={navClass(triggerActive, showLine)}
        onMouseEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
        onClick={() => setOpen((prev) => !prev)}
      >
        {t(menu.triggerKey)}
        <ChevronDown className="h-[13px] w-[13px] opacity-80" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="z-[10000] min-w-[240px] rounded-lg border border-line-subtle bg-white p-1.5 text-ink shadow-overlay"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {menu.items.map((item) => {
          const active = isActive(item.path);
          return (
            <DropdownMenuItem
              key={item.key}
              asChild
              className="rounded-md focus:bg-page focus:text-ink data-[highlighted]:bg-page data-[highlighted]:text-ink"
            >
              <Link
                href={getLocalizedUrl(item.path)}
                className={`group flex min-h-[40px] w-full cursor-pointer items-center justify-between gap-4 px-3 py-2 ${
                  active ? "bg-surface-subtle font-medium text-action-hover" : "font-normal"
                }`}
              >
                <span className="inline-flex items-center gap-2.5 text-sm [&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-ink-muted">
                  {item.icon}
                  {t(item.labelKey)}
                </span>
                <CountPill
                  count={item.countKey ? counts?.[item.countKey] : undefined}
                  active={active}
                  language={language}
                />
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DesktopNav() {
  const { t, language } = useI18n();
  const getLocalizedUrl = useLocalizedUrl();
  const isActive = useIsActive();
  const { data: menuCounts } = useMenuCounts();
  const {
    data: audioguides,
    isLoading: isLoadingAudioguides,
    error: audioguidesError,
  } = useAudioguidesAvailability();

  // Porta condizione audioguide :349 / :831.
  const showAudioguide =
    language !== "sl" &&
    (isLoadingAudioguides || audioguides?.available !== false || !!audioguidesError);

  const counts: NavCounts = { ...menuCounts, audioguides: audioguides?.count };

  const visibleItems = (menu: NavMenu): NavMenu => ({
    ...menu,
    items: menu.items.filter((item) => !item.requiresAudioguides || showAudioguide),
  });

  return (
    // Menu subito dopo il logo (allineato a sinistra come il resto della pagina); a destra solo gli strumenti
    <div className="hidden flex-1 items-center md:flex">
      <nav className="flex items-center gap-0.5" aria-label={t("nav.main")}>
        {MAIN_NAV.map((entry) => {
          if (entry.kind === "menu") {
            return (
              <HoverDropdown
                key={entry.menu.key}
                menu={visibleItems(entry.menu)}
                language={language}
                isActive={isActive}
                getLocalizedUrl={getLocalizedUrl}
                counts={counts}
              />
            );
          }
          const { link } = entry;
          if (link.italianOnly && language !== "it") return null;
          return (
            <Link key={link.key} href={getLocalizedUrl(link.path)} className={navClass(isActive(link.path))}>
              {t(link.labelKey)}
              {link.isNew && <NewBadge />}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
